import { defineStore } from 'pinia';
import { apolloClient } from '@/utils/apollo';
import {
  AppConnectionsScreenDocument,
  GetInstitutionDocument,
} from '@/graphql/generated-types';
import { useDepartmentStore } from '@/stores/department';
import {
  AppConnectionsScreenFragment,
  InstitutionDepartment,
  InstitutionFragment,
} from '@/graphql/custom-types';

type ExtendedInstitution = InstitutionFragment & {
  availableDepartments?: InstitutionDepartment[];
  externalConnections?: AppConnectionsScreenFragment[];
  didLoadExternalConnections: boolean;
};

export const useInstitutionStore = defineStore('institution', {
  state: (): ExtendedInstitution => ({
    id: '',
    name: '',
    locale: '',
    logo: {
      source: '',
    },
    departments: [],
    availableDepartments: [],
    externalConnections: [],
    settings: {
      useExternalMedia: false,
      useExternalMeals: false,
      useExternalWorkSchedule: false,
      screen: {
        __typename: undefined,
        activityHideSignups: false,
        galleryPasswordProtected: false,
        passwordProtection: undefined,
      },
    },
    didLoadExternalConnections: false,
  }),
  actions: {
    async getInstitution() {
      await apolloClient
        .query({
          query: GetInstitutionDocument,
        })
        .then((response) => {
          const departmentStore = useDepartmentStore();
          const availableDepartments = (
            response.data.institution.departments || []
          )
            .filter(
              ({ settings, loginKey, employeeOnly }) =>
                settings?.screenConfig?.departmentSwitcher &&
                !employeeOnly &&
                loginKey !== ''
            )
            .sort(
              (a, b) =>
                (b.id === departmentStore.id ? 1 : 0) -
                (a.id === departmentStore.id ? 1 : 0)
            );

          this.$patch({
            ...response.data.institution,
            availableDepartments,
          });
        })
        .catch((error) => {
          throw new Error('Error getting institution', error);
        });
    },
    async getExternalConnections() {
      await apolloClient
        .query({
          query: AppConnectionsScreenDocument,
        })
        .then((response) => {
          this.externalConnections = response.data?.appConnectionsScreen || [];
        })
        .catch((error) => {
          console.error('Error getting external connections', error);
        })
        .finally(() => {
          this.didLoadExternalConnections = true;

          const departmentStore = useDepartmentStore();
          departmentStore.checkIfValidExternalModule();
        });
    },
    isExternalInstitution(institutionId: string) {
      return this.id !== institutionId;
    },
  },
});
